import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { CreditCard, LogOut, RefreshCw, Download, ArrowLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { orderApi } from "../lib/orderApi";

export default function CounterDashboard() {
  const navigate = useNavigate();
  const { logout } = useAuth();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchOrders = useCallback(async () => {
    try {
      const data = await orderApi.getAllOrders();
      setOrders(data || []);
    } catch {
      console.warn("Could not fetch orders");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
    const interval = setInterval(fetchOrders, 4000);
    return () => clearInterval(interval);
  }, [fetchOrders]);

  const markPaid = async (id) => {
    try {
      await orderApi.updatePaymentStatus(id, "paid");
      toast.success("Payment marked as received");
      fetchOrders();
    } catch {
      toast.error("Failed to update payment status");
    }
  };

  const downloadInvoice = (id) => {
    window.open(orderApi.getInvoiceUrl(id), "_blank");
  };

  return (
    <div className="min-h-screen bg-[#FFFDF9] p-4 sm:p-6 max-w-4xl mx-auto space-y-6">
      {/* HEADER */}
      <div className="flex items-center justify-between pb-4 border-b border-stone-200">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-emerald-100 text-emerald-700 flex items-center justify-center">
            <CreditCard className="w-6 h-6" />
          </div>
          <div>
            <h1 className="font-extrabold text-xl text-[#2C3E50]">Counter & Billing</h1>
            <p className="text-xs text-stone-500">Payments & Invoice Management</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchOrders} className="h-9">
            <RefreshCw className="w-4 h-4" />
          </Button>

          <Button variant="outline" size="sm" onClick={() => navigate("/kitchen")} className="text-xs h-9">
            Kitchen Panel
          </Button>

          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              logout();
              navigate("/login");
            }}
            className="text-xs h-9 text-stone-500"
          >
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* TABS */}
      {loading ? (
        <p className="text-center text-xs text-stone-400 py-10">Loading orders...</p>
      ) : orders.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-3xl border border-stone-200 p-6">
          <CreditCard className="w-12 h-12 text-stone-300 mx-auto mb-2" />
          <p className="font-bold text-stone-600">No counter orders yet</p>
        </div>
      ) : (
        <Tabs defaultValue="pending">
          <TabsList className="mb-4 bg-stone-100 p-1 rounded-2xl">
            <TabsTrigger value="pending" className="rounded-xl text-xs font-bold">
              Pending Payments ({orders.filter((o) => o.payment_status !== "paid").length})
            </TabsTrigger>
            <TabsTrigger value="completed" className="rounded-xl text-xs font-bold">
              Completed ({orders.filter((o) => o.payment_status === "paid").length})
            </TabsTrigger>
          </TabsList>

          {/* PENDING TAB */}
          <TabsContent value="pending" className="space-y-3">
            {orders
              .filter((o) => o.payment_status !== "paid")
              .map((order) => (
                <OrderCard
                  key={order.id}
                  order={order}
                  onPay={markPaid}
                  onDownload={downloadInvoice}
                />
              ))}
          </TabsContent>

          {/* COMPLETED TAB */}
          <TabsContent value="completed" className="space-y-3">
            {orders
              .filter((o) => o.payment_status === "paid")
              .map((order) => (
                <OrderCard
                  key={order.id}
                  order={order}
                  showPay={false}
                  onDownload={downloadInvoice}
                />
              ))}
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}

function OrderCard({ order, onPay, onDownload, showPay = true }) {
  return (
    <div className="bg-white p-5 rounded-3xl border border-stone-200/80 shadow-xs space-y-3">
      <div className="flex items-center justify-between pb-2 border-b border-stone-100">
        <div>
          <h3 className="font-extrabold text-base text-[#2C3E50]">
            Order #{order.order_number}
          </h3>
          <p className="text-xs text-stone-500">
            {order.table_name || "Dine-in"} • {order.customer_name || "Guest"} ({order.customer_phone || "No phone"})
          </p>
        </div>

        <Badge
          className={`text-[10px] font-bold ${
            order.payment_status === "paid"
              ? "bg-emerald-100 text-emerald-800 border-emerald-200"
              : "bg-amber-100 text-amber-800 border-amber-200"
          }`}
        >
          {order.payment_status === "paid" ? "Paid" : "Payment Pending"}
        </Badge>
      </div>

      {/* ITEMS */}
      <div className="space-y-1 text-xs sm:text-sm text-stone-600">
        {order.items?.map((item) => (
          <div key={item.id} className="flex justify-between">
            <span>
              {item.quantity}× {item.product_name}
            </span>
            <span className="font-medium text-stone-900">
              ₹{(Number(item.price) * item.quantity).toFixed(2)}
            </span>
          </div>
        ))}
      </div>

      {/* TOTAL & ACTIONS */}
      <div className="pt-2 border-t border-stone-100 flex items-center justify-between">
        <div>
          <span className="text-xs text-stone-400">Total: </span>
          <span className="font-extrabold text-base text-sky-600">
            ₹{Number(order.total).toFixed(2)}
          </span>
          <span className="text-[10px] text-stone-400 ml-2">
            ({order.payment_method === "pay_now" ? "UPI QR" : "Counter"})
          </span>
        </div>

        <div className="flex gap-2">
          {showPay && order.payment_status !== "paid" && (
            <Button
              onClick={() => onPay(order.id)}
              className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-xs h-8 px-3 rounded-xl"
            >
              <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
              <span>Mark Paid</span>
            </Button>
          )}

          <Button
            variant="outline"
            onClick={() => onDownload(order.id)}
            className="text-xs h-8 px-3 rounded-xl"
          >
            <Download className="w-3.5 h-3.5 mr-1" />
            <span>Invoice</span>
          </Button>
        </div>
      </div>
    </div>
  );
}